import { create } from 'zustand';
import { User, UserResponse } from '../types/auth';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';

const SPA_BASE: string = (import.meta as any).env?.BASE_URL || '/';

// Base de la API: si VITE_API_URL no está definida, se derivan rutas relativas
// a partir de SPA_BASE.
const RAW_BASE: string | undefined = import.meta.env.VITE_API_URL as any;
const API_BASE = (!RAW_BASE || RAW_BASE === 'undefined' || RAW_BASE === 'null' || RAW_BASE === '')
  ? `${SPA_BASE.replace(/\/$/, '')}/api`
  : RAW_BASE.replace(/\/$/, '').endsWith('/api')
    ? RAW_BASE.replace(/\/$/, '')
    : RAW_BASE.replace(/\/$/, '') + '/api';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  checkAuth: () => Promise<void>;
  logout: () => Promise<void>;
  /** Marca el aviso de sugerencias como visto sin volver a pedir /me/. */
  marcarAvisoSugerenciasVisto: () => void;
  /**
   * La interfaz cree que hay sesión y el servidor dijo que no (un 401). Se avisa con
   * `SesionExpirada` en vez de mandar al login, que se llevaría lo que la persona
   * estaba escribiendo.
   */
  sesionExpirada: boolean;
  marcarSesionExpirada: () => void;
  /**
   * Pregunta por la sesión sin pasar por `loading`: `RequireAuth` desmonta la página
   * mientras carga, y esto se usa justamente para no perder lo que hay en pantalla.
   * Devuelve si hay sesión.
   */
  verificarSesion: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: false,
  error: null,
  sesionExpirada: false,

  // Sin usuario no hay nada que avisar: es un visitante del sitio público.
  marcarSesionExpirada: () => set((s) => (s.user ? { sesionExpirada: true } : s)),

  verificarSesion: async () => {
    let response: Response;
    try {
      response = await fetchWithTimeout(`${API_BASE}/me/`, { method: 'GET', credentials: 'include', timeoutMs: 10000 });
    } catch {
      // Sin red no se sabe nada de la sesión: no se la da por cerrada.
      return !get().sesionExpirada;
    }
    if (response.status === 401) {
      get().marcarSesionExpirada();
      return false;
    }
    if (!response.ok) return !get().sesionExpirada;
    const { user }: UserResponse = await response.json();
    const antes = get().user;
    // Volvió a entrar otra persona en el mismo navegador: lo que hay en pantalla es
    // de la anterior, y seguir mostrándolo sería peor que perderlo.
    if (antes && antes.username !== user.username) {
      window.location.reload();
      return true;
    }
    set({ user, sesionExpirada: false });
    return true;
  },

  marcarAvisoSugerenciasVisto: () =>
    set((s) => (s.user ? { user: { ...s.user, vio_aviso_sugerencias: true } } : s)),

  checkAuth: async () => {
    set({ loading: true, error: null });
    try {
      const response = await fetchWithTimeout(`${API_BASE}/me/`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data: UserResponse = await response.json();
        set({ user: data.user, loading: false, sesionExpirada: false });
      } else if (response.status === 401) {
        set({ user: null, loading: false });
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Unknown error';
      console.error('[authStore.checkAuth] Error:', error);
      set({ error, user: null, loading: false });
    }
  },

  logout: async () => {
    try {
      await fetchWithTimeout(`${API_BASE}/logout/`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.warn('[authStore.logout] Error:', err);
    } finally {
      set({ user: null, error: null, sesionExpirada: false });
    }
  },
}));
